import PptxGenJS from 'pptxgenjs';
import { colors } from '@app/services/export/ppt/variables/constants';
import IConnection from '@app/types/data/base/IConnection';
import IConnectionPriceGroupItem from '@app/types/data/base/IConnectionPriceGroupItem';
import IRoutePriceTablePriceItem from '@app/types/data/slides/present/IRoutePriceTableItem';
import SeasonType from '@app/types/base/SeasonType';
import IRoutePriceTableRow from '@app/types/data/slides/present/IRoutePriceTableRow';
import { copy, copyBold, copyMini } from '@app/services/export/ppt/variables/typography';
import global from '@app/filters/global';
import { connectionIconMap } from '@app/services/export/ppt/classes/Connection';
import GlobalsStoreModule from '@app/store/modules/GlobalsStoreModule';
import { getModule as getStoreModule } from 'vuex-module-decorators';
import { AbstractPptComponent, IPptComponentPosition } from '@app/services/export/ppt/classes/AbstractPptComponent';
import TableRow = PptxGenJS.TableRow;
import TableCellProps = PptxGenJS.TableCellProps;
import TextProps = PptxGenJS.TextProps;

interface IProps {
	connection: IConnection;
}

export class RoutePriceTable extends AbstractPptComponent {
	protected readonly slide: PptxGenJS.Slide;

	protected readonly props: IProps;

	protected readonly headerHeight = 0.5;

	protected readonly rowHeight = 0.5;

	protected readonly i18n = getStoreModule(GlobalsStoreModule).i18n;

	protected readonly seasonToColorMap: Record<SeasonType, string> = {
		[SeasonType.DefaultSeason]: colors.defaultSeason,
		[SeasonType.LowSeason]: colors.lowSeason,
		[SeasonType.HighSeason]: colors.highSeason
	};

	public constructor(slide: PptxGenJS.Slide, props: IProps, position: IPptComponentPosition) {
		super(position);
		this.slide = slide;
		this.props = props;
		this.position = position;
	}

	public create(): void {
		this.createTable();
	}

	protected getHeight(): number {
		return this.headerHeight + (this.getRows() || []).length * this.rowHeight;
	}

	private createTable(): void {
		const data = this.getRows();
		const tableRows: TableRow[] = [];

		if (!data) return;

		const defaultHeaderProps: TableCellProps = {
			...copyBold,
			color: colors.blue,
			align: 'center',
			valign: 'middle'
		};

		const defaultCellProps: TableCellProps = {
			...copy,
			color: colors.blue,
			align: 'center',
			valign: 'middle'
		};

		// header
		const tableHeader: TableRow = [{ text: '' }];
		const individualsCellCount = this.getIndividualsCellCount() || 1;

		tableHeader.push({
			text: this.i18n.individuals,
			options: {
				...defaultHeaderProps,
				colspan: individualsCellCount
			}
		});

		if (this.hasSomeGroupPrice()) {
			const textArray: TextProps[] = [{ text: this.i18n.groups, options: { ...copyBold, breakLine: true } }];

			if (this.groupsSubline) {
				textArray.push({ text: this.groupsSubline, options: { ...copyMini } });
			}

			tableHeader.push({
				text: textArray,
				options: { ...defaultHeaderProps }
			});
		}

		tableRows.push(tableHeader);

		// rows

		data.forEach((dataItem, index) => {
			const tableRow: TableRow = [];
			const iconDimension = 0.15;
			const cellCount = dataItem.individuals.length + (dataItem.groups ? 1 : 0) + 1;
			const cellWidth = this.position.w / cellCount;

			// label (empty because icons are used)
			tableRow.push({ text: '' });

			// icons
			this.slide.addImage({
				path: connectionIconMap[dataItem.type],
				y: this.position.y + this.headerHeight + index * this.rowHeight + this.rowHeight / 2 - iconDimension / 2,
				x: this.position.x + cellWidth / 2 - iconDimension / 2,
				w: 15,
				h: 18,
				sizing: {
					type: 'contain',
					w: iconDimension,
					h: iconDimension
				}
			});

			// colored cells (individuals)
			[...dataItem.individuals].forEach(priceItem => {
				tableRow.push({
					text: global.formatPrice(priceItem.price),
					options: {
						...defaultCellProps,
						fill: { color: this.seasonToColorMap[priceItem.type] }
					}
				});
			});

			// single white cell (groups)
			if (dataItem.groups) {
				tableRow.push({
					text: global.formatPrice(dataItem.groups),
					options: {
						...defaultCellProps,
						fill: { color: colors.white }
					}
				});
			}

			tableRows.push(tableRow);
		});

		this.slide.addTable(tableRows, {
			...this.position,
			margin: 0.05,
			rowH: [this.headerHeight, ...Array((this.getRows() || []).length).fill(this.rowHeight)],
			border: { pt: 0.5, color: colors.bluePale }
		});
	}

	// TODO: share this logic, at the moment it is copied more or less from present view component RoutePriceTable.vue
	protected getRows(): IRoutePriceTableRow[] | null {
		if (!this.props.connection.priceGroups || !this.props.connection.priceGroups.length) {
			return null;
		}

		return this.props.connection.priceGroups[0].prices.map(
			(priceGroupItem: IConnectionPriceGroupItem): IRoutePriceTableRow => {
				const individualPrices: IRoutePriceTablePriceItem[] = [];

				if (priceGroupItem.lowSeason) {
					individualPrices.push({
						type: SeasonType.LowSeason,
						price: priceGroupItem.lowSeason
					});
				}

				if (priceGroupItem.defaultSeason) {
					individualPrices.push({
						type: SeasonType.DefaultSeason,
						price: priceGroupItem.defaultSeason
					});
				}

				if (priceGroupItem.highSeason) {
					individualPrices.push({
						type: SeasonType.HighSeason,
						price: priceGroupItem.highSeason
					});
				}

				return {
					type: priceGroupItem.type,
					individuals: individualPrices,
					groups: priceGroupItem.groups
				};
			}
		);
	}

	// all rows are supposed to have the same amount of cells, so we pick it from the first row
	protected getIndividualsCellCount(): number | null {
		const rows = this.getRows();

		if (!rows || !rows[0].individuals) {
			return null;
		}

		return rows[0].individuals.length;
	}

	protected hasSomeGroupPrice(): boolean | null {
		const rows = this.getRows();
		if (!rows) return null;
		return rows.some((item): boolean => !!item.groups);
	}

	protected get groupsSubline(): string | null {
		const filters: Record<string, any> = global as Record<string, any>;

		return filters.formatI18n(this.i18n.xPersonsPlus, {
			persons: this.i18n.minGroupSize
		});
	}
}
